import { validPackTotal } from './oppa-packs.js';

export function moneyCents(text) {
  let number = String(text || '').replace(/[^\d.,-]/g, '');
  const decimal = number.match(/[.,](\d{1,2})$/);
  if (decimal) {
    const position = number.length - decimal[0].length;
    number = number.slice(0, position).replace(/[.,]/g, '') + '.' + decimal[1];
  } else {
    number = number.replace(/[.,]/g, '');
  }
  return number !== '' && Number.isFinite(Number(number)) ? Math.round(Number(number) * 100) : null;
}

export function trayOptions(current, plants, choices, maximum, minimum = 1, increment = 1) {
  if (!Number.isSafeInteger(plants) || plants <= 0 || !Array.isArray(choices) || !choices.length || choices.length > 2 || choices.some((n) => !Number.isSafeInteger(n) || n <= 0) || !Number.isSafeInteger(minimum) || minimum <= 0 || !Number.isSafeInteger(increment) || increment <= 0) return [];
  const limit = Math.min(Math.floor((maximum ?? Infinity) / plants), Math.max(current / plants, ...choices) + 50);
  const result = [0];
  for (let trays = Math.max(Math.ceil(minimum / plants), Math.min(...choices)); trays <= limit; trays++) {
    const quantity = trays * plants;
    if (quantity % increment === 0 && validPackTotal(quantity, plants, choices)) result.push(quantity);
  }
  if (current > 0 && current <= (maximum ?? Infinity) && current >= minimum && current % increment === 0 && validPackTotal(current, plants, choices) && !result.includes(current)) {
    result.push(current);
  }
  return result.sort((a, b) => a - b);
}

function setText(element, value) {
  if (element && element.textContent !== value) element.textContent = value;
}

export function displayedUnitCents(cents, variant, quantity) {
  let result = cents;
  if (variant && cents === variant.price) {
    for (const priceBreak of variant.quantity_price_breaks || []) {
      if (quantity >= priceBreak.quantity && Number.isFinite(priceBreak.price)) result = priceBreak.price;
    }
  }
  return result;
}

function startUnitPrices() {
  document.querySelectorAll('[data-oppa-unit-note]').forEach((note) => {
    const price = note.closest('.oppa-pdp')?.querySelector('[data-price]');
    const plants = Number(note.dataset.plants);
    if (!price || !plants) return;
    const root = note.closest('.oppa-pdp');
    let productData = {};
    try { productData = JSON.parse(root.querySelector('[data-oppa-product-json]')?.textContent || '{}'); } catch {}
    const format = new Intl.NumberFormat(root.dataset.locale || 'sv-SE', { style: 'currency', currency: root.dataset.currency || 'SEK' });
    const update = () => {
      const cents = moneyCents(price.textContent);
      if (cents !== null) {
        const quantity = Number(root.querySelector('[data-pack-quantity]')?.value);
        const id = root.querySelector('input[name="id"]')?.value;
        const variant = productData.variants?.find((entry) => String(entry.id) === id);
        const unitCents = displayedUnitCents(cents, variant, quantity);
        setText(root.querySelector('[data-oppa-tray-price]'), format.format(unitCents * plants / 100));
        setText(note.querySelector('[data-unit-plant-price]'), `${format.format(unitCents / 100)} ${note.dataset.perPlant}`);
        if (Number.isSafeInteger(quantity) && quantity > 0) setText(root.querySelector('[data-pack-total]'), format.format(unitCents * quantity / 100));
      }
    };
    new MutationObserver(update).observe(price, { childList: true, subtree: true, characterData: true });
    root.addEventListener('change', update);
    document.addEventListener('cart:update', update);
    update();
  });
}

function startWholesale() {
  const configNode = document.getElementById('oppa-wsg-pack-config');
  if (!configNode) return;
  let config;
  try { config = JSON.parse(configNode.textContent); } catch { return; }
  if (!config.authorized) return;
  const root = document.querySelector('#wsg-quick-order-form, #wsg-cart');
  if (!root) return;
  const formatter = new Intl.NumberFormat(config.locale || 'sv-SE', { style: 'currency', currency: config.currency || 'SEK' });
  const records = new Map();
  const metadata = new Map();
  let index = {};
  let indexLoaded = false;
  let queued = false;
  const formatCount = (text, count) => text.replace('__COUNT__', String(count));

  async function productData(handle) {
    if (!metadata.has(handle)) {
      const url = new URL(`${window.Shopify?.routes?.root || '/'}products/${encodeURIComponent(handle)}`, window.location.origin);
      url.searchParams.set('view', 'oppa-pack-json');
      const preview = new URL(window.location.href).searchParams.get('preview_theme_id');
      if (preview) url.searchParams.set('preview_theme_id', preview);
      metadata.set(handle, fetch(url, { credentials: 'same-origin' }).then((response) => {
        if (!response.ok) throw new Error('Packing metadata unavailable');
        return response.json();
      }));
    }
    return metadata.get(handle);
  }

  function identify(input) {
    const id = input.getAttribute('item-id') || input.id;
    if (!/^\d+$/.test(id || '')) return null;
    const row = input.closest('.wsg-variant-row, .wsg-cart-product');
    if (!row) return null;
    let handle = index[id];
    if (!handle) {
      const scope = row.closest('[class*="wsgProdIndex-"]') || row;
      for (const anchor of scope.querySelectorAll('a[href*="/products/"]')) {
        const match = new URL(anchor.href).pathname.match(/\/products\/([^/]+)/);
        if (match) { handle = decodeURIComponent(match[1]); break; }
      }
    }
    return handle ? { id, row, handle } : null;
  }

  function showMissing(row, input) {
    input.disabled = true;
    row.querySelectorAll('.wsg-inc, .wsg-dec').forEach((button) => { button.hidden = true; });
    row.dataset.oppaPackInvalid = String(Number(input.value || 0) > 0);
    let message = row.querySelector('[data-pack-warning]');
    if (!message) {
      message = document.createElement('p');
      message.dataset.packWarning = '';
      message.setAttribute('role', 'alert');
      input.parentElement.append(message);
    }
    setText(message, config.data_missing);
  }

  function paint(record) {
    const { input, data, variant, row, select, count, warning, priceNote, stockNote } = record;
    const current = Number(input.value || 0);
    const maximum = Math.min(variant.maximum ?? Infinity, variant.stock ?? Infinity);
    const choices = trayOptions(current, data.plants, data.choices, maximum, variant.minimum, variant.increment);
    const signature = JSON.stringify([current, maximum, choices]);
    if (select.dataset.signature !== signature) {
      select.dataset.signature = signature;
      select.replaceChildren();
      for (const quantity of choices) select.add(new Option(quantity === 0 ? config.choose : `${quantity / data.plants} ${config.trays}`, String(quantity)));
      if (!choices.includes(current)) select.add(new Option(config.choose, String(current)));
      select.value = String(current);
    }
    const valid = current === 0 || choices.includes(current);
    row.dataset.oppaPackInvalid = String(!valid);
    warning.hidden = valid;
    const disabled = input.disabled || !choices.some((quantity) => quantity > 0);
    if (select.disabled !== disabled) select.disabled = disabled;
    setText(count, formatCount(config.plant_count, current));
    if (variant.stock !== null) setText(stockNote, formatCount(config.stock_trays, Math.floor(variant.stock / data.plants)));
    const price = row.querySelector(`.wsg-price-${record.id}, .wsg-item-price_${record.id}`);
    if (price) {
      const attr = price.getAttribute('wsgprice');
      const cents = attr !== null && attr !== '' ? Number(attr) : moneyCents(price.textContent);
      if (Number.isFinite(cents)) setText(priceNote, `${config.per_plant} (${formatter.format(cents * data.plants / 100)} ${config.per_tray})`);
    }
  }

  async function enhance(input) {
    if (input.dataset.oppaPackChecked) return;
    const identity = identify(input);
    if (!identity) return;
    input.dataset.oppaPackChecked = 'pending';
    let data;
    try { data = await productData(identity.handle); } catch {
      input.dataset.oppaPackChecked = 'failed';
      if (index[identity.id]) showMissing(identity.row, input);
      return;
    }
    if (!input.isConnected) return;
    const variant = data.variants?.find((entry) => entry.id === identity.id);
    const isPack = data.unit === 'bratte' || (Array.isArray(data.choices) && data.choices.length) || variant?.review === 'true';
    input.dataset.oppaPackChecked = isPack ? 'pack' : 'other';
    if (!isPack) return;
    if (!variant || variant.review === 'true' || data.price_unit !== 'planta' || !data.plants || !Array.isArray(data.choices) || !data.choices.length) {
      showMissing(identity.row, input);
      return;
    }
    const { row } = identity;
    const control = document.createElement('div');
    control.className = 'oppa-wsg-pack-control';
    const select = document.createElement('select');
    select.setAttribute('aria-label', `${config.choose} (${config.trays})`);
    const count = document.createElement('small');
    const warning = document.createElement('p');
    warning.dataset.packWarning = '';
    warning.hidden = true;
    warning.setAttribute('role', 'alert');
    warning.textContent = config.cart_invalid;
    control.append(select, count, warning);
    input.parentElement.append(control);
    input.classList.add('oppa-wsg-native-quantity');
    input.tabIndex = -1;
    input.setAttribute('aria-hidden', 'true');
    row.querySelectorAll('.wsg-inc, .wsg-dec').forEach((button) => { button.hidden = true; });
    const priceNote = document.createElement('small');
    priceNote.className = 'oppa-wsg-pack-note';
    const price = row.querySelector(`.wsg-price-${identity.id}, .wsg-item-price_${identity.id}`);
    price?.parentElement.append(priceNote);
    const stockNote = document.createElement('small');
    stockNote.className = 'oppa-wsg-pack-note';
    const packingNote = document.createElement('small');
    packingNote.className = 'oppa-wsg-pack-note';
    packingNote.textContent = formatCount(config.plants_per_tray, data.plants);
    control.prepend(packingNote, stockNote);
    const record = { ...identity, input, data, variant, select, count, warning, priceNote, stockNote };
    records.set(input, record);
    select.addEventListener('change', () => {
      input.value = select.value;
      input.dispatchEvent(new Event('input', { bubbles: true }));
      paint(record);
    });
    paint(record);
  }

  function scan() {
    queued = false;
    root.querySelectorAll('.wsg-table input[type="number"], .wsg-cart-table input[type="number"]').forEach(enhance);
    for (const [input, record] of records) {
      if (input.isConnected) paint(record); else records.delete(input);
    }
    const title = root.querySelector('#wsg-title');
    if (title) setText(title, config.quick_order_title);
    const search = root.querySelector('.wsg-search-input');
    if (search && search.placeholder !== config.search_products) {
      search.placeholder = config.search_products;
      search.setAttribute('aria-label', config.search_products);
    }
    root.querySelectorAll('.wsg-head-text').forEach((heading) => {
      const label = heading.textContent.trim();
      if (label === 'Price') setText(heading, config.price_label);
      if (label === 'Total') setText(heading, config.total_label);
    });
  }

  function queueScan() {
    if (queued) return;
    queued = true;
    queueMicrotask(scan);
  }

  new MutationObserver(queueScan).observe(root, { childList: true, subtree: true, characterData: true, attributes: true, attributeFilter: ['wsgprice', 'disabled'] });
  root.addEventListener('input', (event) => { const record = records.get(event.target); if (record) paint(record); });
  root.addEventListener('click', (event) => {
    if (!(event.target instanceof Element)) return;
    if (!event.target.closest('#wsg-checkout-one, #wsg-checkout-two, #wsg-finalize')) return;
    scan();
    const invalid = root.querySelector('[data-oppa-pack-invalid="true"]');
    const pending = Array.from(root.querySelectorAll('[data-oppa-pack-checked="pending"]')).some((input) => Number(input.value || 0) > 0);
    const loadingIndex = !indexLoaded && Array.from(root.querySelectorAll('input[type="number"]')).some((input) => Number(input.value || 0) > 0);
    if (invalid || pending || loadingIndex) {
      event.preventDefault();
      event.stopImmediatePropagation();
      invalid?.querySelector('[data-pack-warning]')?.scrollIntoView({ block: 'center' });
    }
  }, true);
  fetch(config.index_url).then((response) => response.ok ? response.json() : {}).then((result) => { index = result; }).catch(() => {}).finally(() => { indexLoaded = true; queueScan(); });
  scan();
}

function start() { startUnitPrices(); startWholesale(); }
if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start, { once: true }); else start();
